/** The tray body: whichever editor the column has open, over whichever subject is selected. */

import { Stack } from "@/components/ui";

import type { SpaceActivities } from "../../spaces/lib/queries";
import type { AddedActivityRow, SpaceRow } from "../../types";

import { ActivityPanel } from "../ActivityPanel";
import { LayersPanel, type Locks } from "../LayersPanel";
import { SpacePanel } from "../SpacePanel";
import { WiresPanel } from "../WiresPanel";
import type { Placement } from "../lib/arrangement";
import type { StageSelection } from "../SpaceCanvas";

import { AbilityPanel } from "./AbilityPanel";
import { AccessPanel } from "./AccessPanel";
import { CanvasesPanel } from "./CanvasesPanel";
import { CatalogPanel } from "./CatalogPanel";
import { CompositionPanel } from "./CompositionPanel";
import { CuesPanel } from "./CuesPanel";
import { HistoryPanel } from "./HistoryPanel";
import { NeedsPanel } from "./NeedsPanel";
import { StatePanel } from "./StatePanel";
import { PANELS, type PanelId, type Subject } from "./panels";

/** Panels whose body is a live read through one previewed canvas, so the heading names it. */
const NAMES_ITS_CANVAS = new Set<PanelId>(["layers", "state", "ability"]);

function labelOf(panel: PanelId, canvasKind: string): string {
  const found = PANELS.find((one) => one.id === panel);
  const label = found?.label ?? panel;
  return NAMES_ITS_CANVAS.has(panel) && canvasKind !== "" ? `${label} on ${canvasKind}` : label;
}

export interface TrayPanelProps {
  readonly panel: PanelId;
  readonly subject: Subject;
  readonly space: SpaceRow;
  readonly activities: SpaceActivities;
  /** The canvas being drawn, which is what the layer stack is the stack of. */
  readonly canvasKind: string;
  /** The activities that draw on that canvas, back to front. */
  readonly drawn: readonly AddedActivityRow[];
  readonly placements: Readonly<Record<string, Placement>>;
  readonly locks: Locks;
  readonly selectedId: string | null;
  /** The drawn instance a second click picked, read only by Layers. */
  readonly node: StageSelection | null;
  readonly onSelect: (addedActivityId: string | null) => void;
  readonly onLock: (addedActivityId: string, locked: boolean) => void;
  readonly onArrange: (placements: Record<string, Placement>) => void;
  readonly onOpenPanel: (id: PanelId) => void;
  /** An activity was just added, by a click or by a drop. The host takes it from here. */
  readonly onAdded: (addedActivityId: string) => void;
  /** Whether a rectangle means anything on the canvas being drawn. */
  readonly placeable: boolean;
  readonly arranging: boolean;
}

function Body(props: TrayPanelProps) {
  const {
    panel,
    subject,
    space,
    activities,
    canvasKind,
    drawn,
    placements,
    locks,
    selectedId,
    node,
    onSelect,
    onLock,
    onArrange,
    onOpenPanel,
    onAdded,
    placeable,
    arranging,
  } = props;
  const spaceId = space.space.space_id;
  const selected = subject.kind === "activity" ? subject.row : null;
  const clear = (): void => {
    onSelect(null);
  };

  switch (panel) {
    case "space":
      return (
        <SpacePanel
          space={space}
          activities={activities}
          selectedId={selectedId}
          onSelect={(row) => {
            onSelect(row.added_activity_id);
          }}
        />
      );
    case "activity":
      return selected === null ? null : (
        <ActivityPanel
          added={selected}
          activities={activities}
          spaceId={spaceId}
          onClear={clear}
        />
      );
    case "layers":
      return (
        <LayersPanel
          rows={drawn}
          canvasKind={canvasKind}
          placements={placements}
          locks={locks}
          selectedId={selectedId}
          node={node}
          onSelect={onSelect}
          onLock={onLock}
          onCommit={onArrange}
          placeable={placeable}
          busy={arranging}
        />
      );
    case "bindings":
      return <WiresPanel spaceId={spaceId} activities={activities} />;
    case "access":
      return <AccessPanel space={space} activities={activities} selected={selected} />;
    case "canvases":
      return <CanvasesPanel space={space} activities={activities} onOpenPanel={onOpenPanel} />;
    case "needs":
      return (
        <NeedsPanel
          space={space}
          activities={activities}
          selected={selected}
          onOpenPanel={onOpenPanel}
        />
      );
    case "composition":
      return selected === null ? null : (
        <CompositionPanel
          root={selected}
          space={space}
          activities={activities}
          onSelect={onSelect}
          onClear={clear}
        />
      );
    case "ability":
      return <AbilityPanel activities={activities} selectedId={selectedId} canvasKind={canvasKind} />;
    case "state":
      return <StatePanel activities={activities} canvasKind={canvasKind} />;
    case "cues":
      return <CuesPanel activities={activities} />;
    case "catalog":
      return <CatalogPanel spaceId={spaceId} activities={activities} onAdded={onAdded} />;
    case "history":
      return <HistoryPanel space={space} selected={selected} />;
  }
}

export function TrayPanel(props: TrayPanelProps) {
  return (
    <Stack gap={4}>
      <h3 className="cx-tray-title">{labelOf(props.panel, props.canvasKind)}</h3>
      <Body {...props} />
    </Stack>
  );
}
